import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  RubricGeneratorCreateRequestDtoRubricType,
  RubricGeneratorCreateRequestDtoCefrLevel,
} from "@packages/api/madlenAPI.schemas";
import { labelClasses } from "../constants";
import { ThemeColor } from "../OutputLanguageSelector";
import CEFRSelector from "@core/cefr-selector/CEFRSelector";

interface RubricSpecificFieldsProps {
  onChange?: (data: {
    rubricType: RubricGeneratorCreateRequestDtoRubricType;
    totalScore: number;
    assignmentInstructions: string;
    cefrLevel?: RubricGeneratorCreateRequestDtoCefrLevel;
  }) => void;
  initialData?: {
    rubricType?: RubricGeneratorCreateRequestDtoRubricType;
    totalScore?: number;
    assignmentInstructions?: string;
    cefrLevel?: RubricGeneratorCreateRequestDtoCefrLevel;
  };
  themeColor?: ThemeColor;
}

export const RubricSpecificFields: React.FC<RubricSpecificFieldsProps> = ({
  onChange,
  initialData,
  themeColor = "green",
}) => {
  const { t } = useTranslation();

  const [rubricType, setRubricType] =
    useState<RubricGeneratorCreateRequestDtoRubricType>(
      initialData?.rubricType ||
        RubricGeneratorCreateRequestDtoRubricType.ANALYTIC,
    );
  const [totalScore, setTotalScore] = useState<number>(
    initialData?.totalScore || 70,
  );
  const [assignmentInstructions, setAssignmentInstructions] = useState<string>(
    initialData?.assignmentInstructions || "",
  );
  const [cefrLevel, setCefrLevel] =
    useState<RubricGeneratorCreateRequestDtoCefrLevel>(
      initialData?.cefrLevel || RubricGeneratorCreateRequestDtoCefrLevel.A1,
    );

  useEffect(() => {
    if (onChange) {
      onChange({
        rubricType,
        totalScore,
        assignmentInstructions,
        cefrLevel,
      });
    }
  }, [rubricType, totalScore, assignmentInstructions, cefrLevel, onChange]);

  const handleRubricTypeChange = (
    type: RubricGeneratorCreateRequestDtoRubricType,
  ) => {
    setRubricType(type);
  };

  const handleScoreChange = (score: number) => {
    setTotalScore(score);
  };

  const handleInstructionsChange = (instructions: string) => {
    setAssignmentInstructions(instructions);
  };

  const handleCefrLevelChange = (
    level: RubricGeneratorCreateRequestDtoCefrLevel,
  ) => {
    setCefrLevel(level);
  };

  return (
    <div className="space-y-6 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
      {/* Rubric Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("rubric.form.rubricType", "Rubric Type")}
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
              rubricType === RubricGeneratorCreateRequestDtoRubricType.ANALYTIC
                ? `border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20`
                : "border-gray-200 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-400 bg-white dark:bg-dark-600"
            }`}
            onClick={() =>
              handleRubricTypeChange(
                RubricGeneratorCreateRequestDtoRubricType.ANALYTIC,
              )
            }
          >
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">
              {t("rubric.type.analytic", "Analytic Rubric")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t(
                "rubric.type.analyticDesc",
                "Evaluates multiple criteria separately with detailed performance levels",
              )}
            </p>
          </div>

          <div
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
              rubricType === RubricGeneratorCreateRequestDtoRubricType.HOLISTIC
                ? `border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20`
                : "border-gray-200 dark:border-gray-600 hover:border-green-400 dark:hover:border-green-400 bg-white dark:bg-dark-600"
            }`}
            onClick={() =>
              handleRubricTypeChange(
                RubricGeneratorCreateRequestDtoRubricType.HOLISTIC,
              )
            }
          >
            <h4 className="font-semibold text-base mb-2 text-gray-900 dark:text-gray-100">
              {t("rubric.type.holistic", "Holistic Rubric")}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t(
                "rubric.type.holisticDesc",
                "Provides an overall assessment with general performance descriptions",
              )}
            </p>
          </div>
        </div>
      </div>

      {/* CEFR Level Selector */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("rubric.form.cefrLevel", "CEFR Level")}
        </label>
        <CEFRSelector
          selectedLevel={cefrLevel}
          onLevelChange={handleCefrLevelChange}
          themeColor="green"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t(
            "rubric.form.cefrLevelDescription",
            "Select the target CEFR level for this rubric",
          )}
        </p>
      </div>

      {/* Total Score Configuration */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("rubric.form.totalScore", "Total Score")}: {totalScore}
        </label>
        <div className="px-3 py-2 bg-gray-50 dark:bg-dark-600 rounded-xl">
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={totalScore}
            onChange={(e) => handleScoreChange(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-green-600 dark:accent-green-500"
          />
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-2">
            <span>10</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Assignment Instructions */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t("rubric.form.assignmentInstructions", "Assignment Instructions")}
        </label>
        <textarea
          value={assignmentInstructions}
          onChange={(e) => handleInstructionsChange(e.target.value)}
          className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 text-base focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-dark-500 dark:text-gray-100 transition-all duration-200 min-h-[120px] resize-y placeholder-gray-500 dark:placeholder-gray-400"
          placeholder={t(
            "rubric.form.assignmentInstructionsPlaceholder",
            "Describe your assignment in detail...",
          )}
          rows={4}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t(
            "rubric.form.assignmentInstructionsHelp",
            "Provide detailed instructions for the assignment to create a more accurate rubric",
          )}
        </p>
      </div>
    </div>
  );
};

export default RubricSpecificFields;
